import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogTitle,
	Label,
	TextareaField,
} from "@exopengithub/exokit";
import { Button } from "@exopengithub/exokit/v2";
import {
	showErrorNotification,
	showSuccessNotification,
} from "molecules/notifications/notificationEvents";
import { DialogActions } from "molecules/dialog/DialogActions";
import { Input } from "molecules/input/Input";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";

import {
	useCreateSkillMutation,
	useUpdateSkillMutation,
} from "../../ai-chat/api/aiSkillsApi";
import type { SkillResponse } from "../../ai-chat/api/skillTypes";
import {
	FORBIDDEN_NAME_WORDS,
	SKILL_DESCRIPTION_MAX,
	SKILL_INSTRUCTIONS_MAX,
	SKILL_NAME_MAX,
	SKILL_NAME_PATTERN,
	XML_TAG_PATTERN,
} from "../../ai-chat/api/skillTypes";

interface SkillFormDialogProps {
	open: boolean;
	onClose: () => void;
	companyDomainId: string;
	skill?: SkillResponse | undefined;
}

export function SkillFormDialog({
	open,
	onClose,
	companyDomainId,
	skill,
}: SkillFormDialogProps) {
	const { t } = useTranslation();
	const isEditing = !!skill;

	const nameId = useId();

	const [name, setName] = useState(skill?.name ?? "");
	const [description, setDescription] = useState(skill?.description ?? "");
	const [instructions, setInstructions] = useState(skill?.instructions ?? "");

	const [nameError, setNameError] = useState<string | null>(null);
	const [descriptionError, setDescriptionError] = useState<string | null>(null);
	const [instructionsError, setInstructionsError] = useState<string | null>(
		null,
	);

	const [createSkill, { isLoading: isCreating }] = useCreateSkillMutation();
	const [updateSkill, { isLoading: isUpdating }] = useUpdateSkillMutation();

	const isSaving = isCreating || isUpdating;

	function validateName(value: string): string | null {
		if (!value.trim()) return t("Name is required");
		if (value.length > SKILL_NAME_MAX)
			return t("Name must be at most {{max}} characters", {
				max: SKILL_NAME_MAX,
			});
		if (!SKILL_NAME_PATTERN.test(value))
			return t("Name may only contain lowercase letters, numbers, and hyphens");
		if (FORBIDDEN_NAME_WORDS.some((w) => value.toLowerCase().includes(w)))
			return t("Name must not contain forbidden words");
		return null;
	}

	function validateDescription(value: string): string | null {
		if (!value.trim()) return t("Description is required");
		if (value.length > SKILL_DESCRIPTION_MAX)
			return t("Description must be at most {{max}} characters", {
				max: SKILL_DESCRIPTION_MAX,
			});
		if (XML_TAG_PATTERN.test(value))
			return t("Description must not contain XML tags");
		return null;
	}

	function validateInstructions(value: string): string | null {
		if (!value.trim()) return t("Instructions are required");
		if (value.length > SKILL_INSTRUCTIONS_MAX)
			return t("Instructions must be at most {{max}} characters", {
				max: SKILL_INSTRUCTIONS_MAX,
			});
		return null;
	}

	function validate(): boolean {
		const ne = validateName(name);
		const de = validateDescription(description);
		const ie = validateInstructions(instructions);
		setNameError(ne);
		setDescriptionError(de);
		setInstructionsError(ie);
		return !ne && !de && !ie;
	}

	async function handleSave() {
		if (!validate()) return;
		try {
			if (isEditing) {
				await updateSkill({
					companyDomainId,
					skillId: skill.id,
					name,
					description,
					instructions,
				}).unwrap();
				showSuccessNotification({ message: t("Skill updated") });
			} else {
				await createSkill({
					companyDomainId,
					name,
					description,
					instructions,
				}).unwrap();
				showSuccessNotification({ message: t("Skill created") });
			}
			onClose();
		} catch {
			showErrorNotification({
				message: isEditing
					? t("Failed to update skill")
					: t("Failed to create skill"),
			});
		}
	}

	return (
		<Dialog open={open} onClose={onClose}>
			<DialogContent>
				<DialogTitle>
					{isEditing ? t("Edit skill") : t("Add skill")}
				</DialogTitle>
				<DialogBody className="space-y-4">
					<div>
						<Label htmlFor={nameId}>{t("Name")}</Label>
						<Input
							id={nameId}
							value={name}
							onChange={(e) => {
								setName(e.target.value);
								setNameError(null);
							}}
							placeholder="my-skill-name"
							aria-invalid={!!nameError}
							hint={nameError ?? undefined}
						/>
					</div>
					<div>
						<TextareaField
							label={t("Description")}
							value={description}
							onChange={(e) => {
								setDescription(e.target.value);
								setDescriptionError(null);
							}}
							rows={2}
							aria-invalid={!!descriptionError}
							hint={descriptionError ?? undefined}
						/>
					</div>
					<div>
						<TextareaField
							label={t("Instructions")}
							value={instructions}
							onChange={(e) => {
								setInstructions(e.target.value);
								setInstructionsError(null);
							}}
							rows={8}
							aria-invalid={!!instructionsError}
							hint={instructionsError ?? undefined}
						/>
						<div className="text-xs text-gray-400 text-right mt-1">
							{instructions.length.toLocaleString()} /{" "}
							{SKILL_INSTRUCTIONS_MAX.toLocaleString()}
						</div>
					</div>
				</DialogBody>
				<DialogActions withCancelButton>
					<Button onClick={handleSave} disabled={isSaving} isLoading={isSaving}>
						{t("Save")}
					</Button>
				</DialogActions>
			</DialogContent>
		</Dialog>
	);
}
